function anchorTonalPalette(anchor){
  const hex=anchor&&anchor.length===7?anchor:"#C9A84C";
  const ri=parseInt(hex.slice(1,3),16)/255,gi=parseInt(hex.slice(3,5),16)/255,bi=parseInt(hex.slice(5,7),16)/255;
  /* linearise sRGB */
  const lin=c=>c<=0.04045?c/12.92:Math.pow((c+0.055)/1.055,2.4);
  const rl=lin(ri),gl=lin(gi),bl=lin(bi);
  /* RGB → OKLab via LMS (approximate) */
  const l_=Math.cbrt(0.4122214708*rl+0.5363325363*gl+0.0514459929*bl);
  const m_=Math.cbrt(0.2119034982*rl+0.6806995451*gl+0.1073969566*bl);
  const s_=Math.cbrt(0.0883024619*rl+0.2024326073*gl+0.6892649308*bl);
  const L0=0.2104542553*l_+0.7936177850*m_-0.0040720468*s_;
  const a0=1.9779984951*l_-2.4285922050*m_+0.4505937099*s_;
  const b0=0.0259040371*l_+0.7827717662*m_-0.8086757660*s_;
  /* polar chroma & hue */
  const C0=Math.sqrt(a0*a0+b0*b0);
  const hueRad=Math.atan2(b0,a0);
  /* 12 lightness stops from ~0.04 to ~0.97 */
  const STOPS=[0.04,0.08,0.14,0.22,0.33,0.46,0.59,0.72,0.82,0.89,0.95,0.97];
  /* OKLab → linear RGB → sRGB hex */
  const oklab2hex=(L,a,b)=>{
    const l2=L+0.3963377774*a+0.2158037573*b;
    const m2=L-0.1055613458*a-0.0638541728*b;
    const s2=L-0.0894841775*a-1.2914855480*b;
    const l3=l2*l2*l2,m3=m2*m2*m2,s3=s2*s2*s2;
    let R=+4.0767416621*l3-3.3077115913*m3+0.2309699292*s3;
    let G=-1.2684380046*l3+2.6097574011*m3-0.3413193965*s3;
    let B=-0.0041960863*l3-0.7034186147*m3+1.7076147010*s3;
    /* gamut clamp */
    R=Math.max(0,Math.min(1,R));G=Math.max(0,Math.min(1,G));B=Math.max(0,Math.min(1,B));
    /* gamma encode */
    const gam=c=>c<=0.0031308?12.92*c:1.055*Math.pow(c,1/2.4)-0.055;
    const rr=Math.round(gam(R)*255),gg=Math.round(gam(G)*255),bb=Math.round(gam(B)*255);
    return`#${rr.toString(16).padStart(2,"0")}${gg.toString(16).padStart(2,"0")}${bb.toString(16).padStart(2,"0")}`;
  };
  return STOPS.map(L=>{
    /* Scale chroma: peak at mid-lightness, taper toward extremes */
    const chromaScale=Math.max(0,1-Math.pow((L-0.5)/0.45,2));
    const C=C0*chromaScale;
    return oklab2hex(L,C*Math.cos(hueRad),C*Math.sin(hueRad));
  });
}
/* Derive primary (ink) and accent from anchor via tonal palette:
   - accent  = the anchor colour itself (mid-tone, step ~5-6)
   - primary = darkest usable tone (step 1-2 area) */
function anchorDerivedColors(anchor){
  const palette=anchorTonalPalette(anchor);
  return { primary:palette[3], accent:anchor&&anchor.length===7?anchor:"#C9A84C", palette };
}

function brandChartPalette(accent){
  const hex=accent&&accent.length===7?accent:"#C9A84C";
  const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16);
  /* Convert RGB → HSL */
  const r1=r/255,g1=g/255,b1=b/255;
  const mx=Math.max(r1,g1,b1),mn=Math.min(r1,g1,b1);
  let h=0,s=0,l=(mx+mn)/2;
  if(mx!==mn){
    const d=mx-mn;
    s=l>0.5?d/(2-mx-mn):d/(mx+mn);
    if(mx===r1)h=((g1-b1)/d+(g1<b1?6:0))/6;
    else if(mx===g1)h=((b1-r1)/d+2)/6;
    else h=((r1-g1)/d+4)/6;
  }
  /* Generate 10 colours by rotating hue and varying saturation/lightness */
  const hsl2hex=(h2,s2,l2)=>{
    h2=((h2%1)+1)%1;
    const a=s2*Math.min(l2,1-l2);
    const f=n=>{const k=(n+h2*12)%12;const c=l2-a*Math.max(Math.min(k-3,9-k,1),-1);return Math.round(c*255).toString(16).padStart(2,"0");};
    return`#${f(0)}${f(8)}${f(4)}`;
  };
  return [
    hex,                                     /* 0  accent itself        */
    hsl2hex(h+0.08, Math.min(s*1.1,1), Math.min(l*1.1,0.65)),  /* 1  slight hue shift     */
    hsl2hex(h+0.55, Math.min(s*0.9,1), Math.min(l*0.95,0.55)), /* 2  complementary zone    */
    hsl2hex(h+0.17, Math.min(s*1.05,1),Math.min(l*1.05,0.6)),  /* 3  analogous warm       */
    hsl2hex(h+0.33, Math.min(s*0.85,1),Math.min(l*0.9,0.5)),   /* 4  triadic              */
    hsl2hex(h+0.67, Math.min(s*0.8,1), Math.min(l*1.0,0.55)),  /* 5  triadic 2            */
    hsl2hex(h+0.42, s*0.7, Math.min(l*1.15,0.7)),               /* 6  muted mid            */
    hsl2hex(h+0.25, Math.min(s*1.2,1), Math.max(l*0.75,0.35)),  /* 7  deep analogous       */
    hsl2hex(h+0.83, s*0.6, Math.min(l*1.2,0.72)),               /* 8  soft far             */
    hsl2hex(h+0.12, Math.min(s*0.95,1),Math.max(l*0.8,0.38)),   /* 9  rich near            */
  ];
}

export { anchorTonalPalette, anchorDerivedColors, brandChartPalette };
